import type { OrderDetail, OrderSummary } from '../api/types';

/**
 * A-14 — the order queue's Excel download: an Orders sheet (one row per order,
 * the queue's own columns) and a Lines sheet (one row per order line, keyed back
 * to its order by reference).
 *
 * Built in the browser from what the screen already has plus each order's
 * detail — no export endpoint. The row builders are kept apart from the
 * workbook so they can be tested without exceljs.
 */

export interface ExportColumn {
  header: string;
  key: string;
  width: number;
  /** Excel number format — money and quantities stay numeric so they can be summed. */
  numFmt?: string;
}

type Cell = string | number | Date | null;
export type ExportRow = Record<string, Cell>;

const MONEY_FMT = '#,##0.00';

/**
 * Money arrives as a fixed-precision string (`packages/money`). A spreadsheet
 * cell has no such type — Excel itself holds a double — so the value is handed
 * over as a number for the sheet alone, never used for arithmetic here.
 */
const moneyCell = (amount: string | null): number | null => (amount === null || amount === '' ? null : Number(amount));

/** `UNDER_REVIEW` reads "Under review" — the same wording the queue's filter uses. */
const statusText = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

export const ORDER_COLUMNS: ExportColumn[] = [
  { header: 'Order reference', key: 'reference', width: 18 },
  { header: 'Placed by', key: 'placedBy', width: 22 },
  { header: 'Company', key: 'company', width: 28 },
  { header: 'Status', key: 'status', width: 20 },
  { header: 'Total pieces', key: 'pieces', width: 12, numFmt: '0' },
  { header: 'Lines', key: 'variants', width: 8, numFmt: '0' },
  { header: 'Currency', key: 'currency', width: 9 },
  { header: 'Submitted price', key: 'submittedTotal', width: 16, numFmt: MONEY_FMT },
  { header: 'Agreed price', key: 'agreedTotal', width: 16, numFmt: MONEY_FMT },
  { header: 'Submitted at', key: 'submittedAt', width: 18, numFmt: 'yyyy-mm-dd hh:mm' },
  { header: 'Expected delivery', key: 'expectedDelivery', width: 17 },
];

/**
 * One row per order line, with everything a bench needs to make the piece — it must read on its own, so each
 * row repeats the order's reference, date and company rather than relying on the Orders sheet. Specification
 * columns are the catalogue's values for the line's variant and stay empty where the catalogue has none.
 */
export const LINE_COLUMNS: ExportColumn[] = [
  { header: 'Order reference', key: 'reference', width: 18 },
  { header: 'Order date', key: 'orderDate', width: 18, numFmt: 'yyyy-mm-dd hh:mm' },
  { header: 'Order status', key: 'status', width: 18 },
  { header: 'Company', key: 'company', width: 28 },
  { header: 'Placed by', key: 'placedBy', width: 22 },
  { header: 'Expected delivery', key: 'expectedDelivery', width: 17 },
  { header: 'Line', key: 'lineNumber', width: 6, numFmt: '0' },
  { header: 'Product name', key: 'description', width: 30 },
  { header: 'Design code', key: 'baseCode', width: 16 },
  { header: 'Variant code', key: 'variantCode', width: 34 },
  { header: 'Quantity', key: 'quantity', width: 9, numFmt: '0' },
  { header: 'Size', key: 'size', width: 8 },
  { header: 'Metal', key: 'metalType', width: 14 },
  { header: 'Purity', key: 'purity', width: 10 },
  { header: 'Metal colour', key: 'metalColour', width: 14 },
  { header: 'Net metal weight (g)', key: 'netMetalWeightG', width: 14 },
  { header: 'Gross weight (g)', key: 'grossWeightG', width: 14 },
  { header: 'Total carat weight', key: 'totalCaratWeight', width: 14 },
  { header: 'Centre stone carat', key: 'centreCarat', width: 14 },
  { header: 'Stone', key: 'gemstone', width: 14 },
  { header: 'Stone origin', key: 'stoneOrigin', width: 14 },
  { header: 'Stone shape', key: 'stoneShape', width: 12 },
  { header: 'Stone cut', key: 'stoneCut', width: 12 },
  { header: 'Stone colour', key: 'stoneColour', width: 12 },
  { header: 'Stone clarity', key: 'stoneClarity', width: 12 },
  { header: 'Setting', key: 'setting', width: 14 },
  { header: 'Finish', key: 'finish', width: 14 },
  { header: 'Clasp / backing', key: 'closure', width: 16 },
  { header: 'Remark', key: 'remark', width: 40 },
  { header: 'Currency', key: 'currency', width: 9 },
  { header: 'Submitted unit price', key: 'submittedUnitPrice', width: 18, numFmt: MONEY_FMT },
  { header: 'Agreed unit price', key: 'agreedUnitPrice', width: 18, numFmt: MONEY_FMT },
  { header: 'Total price', key: 'lineTotal', width: 16, numFmt: MONEY_FMT },
];

/**
 * One row per order (the queue's own columns). It carries no product, variant or per-variant quantity: those are
 * different for every line and a cell cannot hold several without running them together, so they live on the
 * Lines sheet, one row per product-variant line.
 */
export function orderRows(orders: OrderSummary[]): ExportRow[] {
  return orders.map((o) => ({
    reference: o.reference,
    placedBy: o.placedBy,
    company: o.retailer,
    status: statusText(o.status),
    pieces: o.pieces,
    variants: o.lines,
    currency: o.currency,
    submittedTotal: moneyCell(o.submittedTotal),
    agreedTotal: moneyCell(o.agreedTotal),
    submittedAt: new Date(o.submittedAt),
    expectedDelivery: o.expectedDeliveryDate
      ? `${o.expectedDeliveryDate}${o.isCommittedDate ? '' : ' (estimate)'}`
      : null,
  }));
}

/** In the same order as `orders`, then by line number — the Lines sheet reads alongside the Orders sheet. */
export function lineRows(orders: OrderSummary[], details: ReadonlyMap<string, OrderDetail>): ExportRow[] {
  return orders.flatMap((o) => {
    const d = details.get(o.id);
    if (!d) return [];
    return [...d.lines].sort((a, b) => a.lineNumber - b.lineNumber).map((l) => ({
      reference: o.reference,
      orderDate: new Date(o.submittedAt),
      status: statusText(o.status),
      company: o.retailer,
      placedBy: o.placedBy,
      expectedDelivery: o.expectedDeliveryDate
        ? `${o.expectedDeliveryDate}${o.isCommittedDate ? '' : ' (estimate)'}` : null,
      lineNumber: l.lineNumber,
      description: l.description,
      baseCode: l.baseCode,
      variantCode: l.variantCode,
      quantity: l.quantity,
      size: l.size,
      metalType: l.spec?.metalType ?? null,
      purity: l.spec?.purity ?? null,
      metalColour: l.spec?.metalColour ?? null,
      netMetalWeightG: l.spec?.netMetalWeightG ?? null,
      grossWeightG: l.spec?.grossWeightG ?? null,
      totalCaratWeight: l.spec?.totalCaratWeight ?? null,
      centreCarat: l.spec?.centreCarat ?? null,
      gemstone: l.spec?.gemstone ?? null,
      stoneOrigin: l.spec?.stoneOrigin ?? null,
      stoneShape: l.spec?.stoneShape ?? null,
      stoneCut: l.spec?.stoneCut ?? null,
      stoneColour: l.spec?.stoneColour ?? null,
      stoneClarity: l.spec?.stoneClarity ?? null,
      setting: l.spec?.setting ?? null,
      finish: l.spec?.finish ?? null,
      closure: [l.spec?.clasp, l.spec?.backing].filter(Boolean).join(' / ') || null,
      remark: l.remark,
      currency: d.currency,
      submittedUnitPrice: moneyCell(l.submittedUnitPrice),
      agreedUnitPrice: moneyCell(l.agreedUnitPrice),
      lineTotal: moneyCell(l.lineTotal),
    }));
  });
}

/**
 * One order's detail in the list's shape, for the order page's own download.
 * The detail carries no line or piece counts, so they are counted here; it
 * carries no submitter either, so "Placed by" is taken from the list when the
 * caller has it.
 */
export function summaryFromDetail(d: OrderDetail, placedBy: string | null = null): OrderSummary {
  return {
    id: d.id,
    reference: d.reference,
    retailerId: d.retailer.id,
    retailer: d.retailer.name,
    placedBy,
    lines: d.lines.length,
    items: [...new Set(d.lines.map((l) => l.description ?? l.variantCode))],
    pieces: d.lines.reduce((n, l) => n + l.quantity, 0),
    status: d.status,
    storedStatus: d.storedStatus,
    currency: d.currency,
    submittedTotal: d.submittedTotal,
    agreedTotal: d.agreedTotal,
    submittedAt: d.submittedAt,
    acceptanceExpiresAt: d.acceptanceExpiresAt,
    expectedDeliveryDate: d.expectedDeliveryDate,
    isCommittedDate: d.isCommittedDate,
    awaitingReply: d.awaitingReply,
  };
}

/** `miraki-orders-2026-09-24.xlsx`, `miraki-orders-selected-…` for a selection,
 *  or `miraki-order-MK-0012-…` for one order. */
export function exportFileName(scope: 'all' | 'selected' | { reference: string }, now = new Date()): string {
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (typeof scope === 'object') return `miraki-order-${scope.reference.replace(/[^A-Za-z0-9_-]/g, '_')}-${day}.xlsx`;
  return `miraki-orders${scope === 'selected' ? '-selected' : ''}-${day}.xlsx`;
}

/**
 * Builds the workbook and hands it to the browser as a download. exceljs is
 * loaded here, on first use, so it stays out of the bundle every other screen
 * pays for.
 */
export async function downloadOrdersWorkbook(
  orders: OrderSummary[],
  details: ReadonlyMap<string, OrderDetail>,
  fileName: string,
): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Miraki';
  wb.created = new Date();

  const addSheet = (name: string, columns: ExportColumn[], rows: ExportRow[]) => {
    const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
    ws.columns = columns.map((c) => ({
      header: c.header, key: c.key, width: c.width, ...(c.numFmt ? { style: { numFmt: c.numFmt } } : {}),
    }));
    ws.getRow(1).font = { bold: true };
    ws.addRows(rows);
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
  };
  // The manufacturing sheet comes first: it is the one that can be used on its own.
  addSheet('Lines', LINE_COLUMNS, lineRows(orders, details));
  addSheet('Orders', ORDER_COLUMNS, orderRows(orders));

  const buffer = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // After the click has handed the blob to the download, not before.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
